import React, { useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { ActivityMedia } from '@eduwrite/shared';

interface ActivityMediaRendererProps {
    media: ActivityMedia[];
    placement?: 'before' | 'alongside' | 'after';
}

/**
 * Renders a list of ActivityMedia assets filtered by their placement.
 * Called by activity components to show supporting images, audio clips etc.
 *
 * Design rules (from activity-framework.md):
 *  - Audio is always opt-in (tap to play), never auto-play
 *  - Images must have label text for accessibility
 *  - Video is tap-to-play via MediaVideo (react-native-video)
 */
export const ActivityMediaRenderer: React.FC<ActivityMediaRendererProps> = ({
    media,
    placement = 'before',
}) => {
    const filtered = media.filter(m => (m.placement || 'before') === placement);
    if (filtered.length === 0) return null;

    return (
        <View style={styles.container}>
            {filtered.map((item, i) => (
                <MediaItem key={i} item={item} />
            ))}
        </View>
    );
};

const MediaItem: React.FC<{ item: ActivityMedia }> = ({ item }) => {
    switch (item.type) {
        case 'image':
            return <MediaImage item={item} />;
        case 'audio':
            return <MediaAudio item={item} />;
        case 'video':
            return <MediaVideo url={item.url} label={item.label} />;
        case 'interactive_story':
            return (
                <View style={styles.storyPill}>
                    <Text style={styles.storyText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>📖 Story: {item.label || 'Interactive Story'}</Text>
                </View>
            );
        default:
            return null;
    }
};

const MediaImage: React.FC<{ item: ActivityMedia }> = ({ item }) => (
    <View style={styles.imageWrapper}>
        <Image
            source={{ uri: item.url }}
            style={styles.image}
            resizeMode="contain"
            accessibilityLabel={item.label || 'Activity image'}
            accessibilityRole="image"
        />
        {item.label && <Text style={styles.caption} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{item.label}</Text>}
    </View>
);

import { AudioService } from '../services/AudioService';
import { MediaVideo } from './MediaVideo';

const MediaAudio: React.FC<{ item: ActivityMedia }> = ({ item }) => {
    const [playing, setPlaying] = useState(false);

    const handlePlay = () => {
        if (!item.url) return;
        setPlaying(true);
        AudioService.play(item.url, () => {
            setPlaying(false);
        }).catch(() => {
            setPlaying(false);
        });
    };

    return (
        <View style={styles.audioPill}>
            <TouchableOpacity
                testID="audio-play-btn"
                style={[styles.playBtn, playing && styles.playBtnActive]}
                onPress={handlePlay}
                accessibilityLabel={item.label || 'Play audio'}
                accessibilityRole="button"
                accessibilityHint="Double tap to play or pause the audio"
            >
                <Text style={styles.playIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{playing ? '⏸' : '▶'}</Text>
            </TouchableOpacity>
            <Text style={styles.audioLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{playing ? 'Playing...' : (item.label || 'Tap to listen')}</Text>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { gap: 12, marginBottom: 16 },

    imageWrapper: {
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    image: { width: '100%', height: 200 },
    caption: {
        paddingHorizontal: 12, paddingVertical: 6,
        fontSize: 12, color: '#64748B', fontStyle: 'italic', textAlign: 'center',
    },

    audioPill: {
        flexDirection: 'row', alignItems: 'center', gap: 12,
        backgroundColor: '#F0FDF4', borderRadius: 12,
        borderWidth: 1, borderColor: '#86EFAC', padding: 12,
    },
    playBtn: {
        width: 56, height: 56, borderRadius: 28,
        backgroundColor: '#10B981', alignItems: 'center', justifyContent: 'center',
    },
    playBtnActive: { backgroundColor: '#059669' },
    playIcon: { fontSize: 18, color: '#fff' },
    audioLabel: { fontSize: 15, fontWeight: '600', color: '#065F46', flex: 1 },

    storyPill: {
        backgroundColor: '#FDF4FF', borderRadius: 12,
        borderWidth: 1, borderColor: '#E879F9', padding: 14,
    },
    storyText: { fontSize: 15, fontWeight: '600', color: '#7E22CE' },
});
